import { useId, useRef } from 'react';
import { Box } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { RwAlert, RwButton, RwModal } from '@/common/presentation/components/rw';

export interface ArchiveBlockDialogProps {
  open: boolean;

  blockName: string;

  isArchiving: boolean;

  /** A failed archive (e.g. 409 "still referenced") — shown inline so the dialog is the one channel. */
  error: string | null;

  onClose: () => void;

  onConfirm: () => void;
}

/**
 * Archive confirmation (LLAMS-617 QA-DB09-2). Mirrors `UnarchiveBlockDialog` and the template
 * library's archive confirm: the card menu never archives on the first click, and a failure stays
 * here (inline) instead of surfacing as a page-level alert.
 */
export function ArchiveBlockDialog({ open, blockName, isArchiving, error, onClose, onConfirm }: ArchiveBlockDialogProps) {
  const descriptionId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const context = `"${blockName}" will be removed from the default block library list. You can restore it later from Show archived.`;

  return (
    <RwModal
      open={open}
      onClose={() => {
        if (!isArchiving) onClose();
      }}
      title="Archive this block?"
      context={context}
      dismissable={!isArchiving}
      initialFocusRef={cancelRef}
      testId="document-builder:archive-block-dialog:root"
      descriptionId={descriptionId}
      footer={
        <>
          <RwButton
            ref={cancelRef}
            tier="secondary"
            onClick={onClose}
            disabled={isArchiving}
            data-testid="document-builder:archive-block-dialog:cancel"
          >
            Cancel
          </RwButton>

          <RwButton
            tier="primary"
            destructive
            onClick={onConfirm}
            disabled={isArchiving}
            loading={isArchiving}
            data-testid="document-builder:archive-block-dialog:confirm"
            aria-label="Archive block"
          >
            Archive
          </RwButton>
        </>
      }
    >
      <Box component="span" id={descriptionId} sx={visuallyHidden}>
        {context}
      </Box>
      {error ? <RwAlert tone="critical" title={error} data-testid="document-builder:archive-block-dialog:error" /> : null}
    </RwModal>
  );
}
